import React, { ChangeEvent, FormEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router-dom";
import ReactModal from "react-modal";

import withTitle from "../../withTitle";
import { V2 } from "../../../Api";
import { deepCopy, diffObjects, formatTelephonePartial } from "../../../Utilities";
import { allergySeverityToText, getMaritalStatusValues } from "../../../TextTables";

import Indicator from "../../Indicator";
import ValidateInput from "../../ValidateInput";

import "../../../form.css";

type ItemChange<T> = {
    type: "add";
    id: string;
    data: T;
} | {
    type: "delete";
    id: string;
} | {
    type: "change";
    id: string;
    data: Partial<T>;
};

interface IPatientEditViewProps extends RouteComponentProps<any> {

}

interface IPatientEditViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    isAllergyDialogOpen: boolean;
    isFlagDialogOpen: boolean;
    hasChanged: boolean;

    originalData: any;
    data: any;

    flags: any[];
    flagChanges: { [id: string]: ItemChange<any> };
    selectedFlag: number | null;
    isFlagsLoading: boolean | string;
    availableFlags: any[];
    flagAddList: string[];

    allergies: any[];
    allergyChanges: { [id: string]: ItemChange<any> };
    selectedAllergy: number | null;

    newAllergySeverity: string;
    newAllergyValue: string;
    newAllergyReaction: string;

    insuranceCompanies: any[];
}


class PatientEditView extends React.Component<IPatientEditViewProps, IPatientEditViewState> {
    constructor(props: Readonly<IPatientEditViewProps>) {
        super(props);

        const state: IPatientEditViewState = {
            isLoading: true,
            isSubmitting: false,
            isAllergyDialogOpen: false,
            isFlagDialogOpen: false,
            hasChanged: false,

            originalData: {},
            data: {},

            flags: [],
            flagChanges: {},
            selectedFlag: null,
            isFlagsLoading: false,
            availableFlags: [],
            flagAddList: [],

            allergies: [],
            allergyChanges: {},
            selectedAllergy: null,

            newAllergySeverity: "",
            newAllergyValue: "",
            newAllergyReaction: "",

            insuranceCompanies: []
        };

        this.state = state;

        this.formInputChanged = this.formInputChanged.bind(this);
        this.formInputChangedNumber = this.formInputChangedNumber.bind(this);
        this.dialogInputChanged = this.dialogInputChanged.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.goBack = this.goBack.bind(this);
        this.toggleAllergyDialog = this.toggleAllergyDialog.bind(this);
        this.saveAllergy = this.saveAllergy.bind(this);
        this.editAllergy = this.editAllergy.bind(this);
        this.deleteAllergy = this.deleteAllergy.bind(this);
        this.toggleFlagDialog = this.toggleFlagDialog.bind(this);
        this.deleteFlag = this.deleteFlag.bind(this);
        this.saveFlag = this.saveFlag.bind(this);
    }

    public componentDidMount() {
        this.loadData();
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="patient-page-body">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }
        if (typeof this.state.isLoading === "string") {
            return <div className="patient-page-body">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        const data = this.state.data;
        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                {this.state.isAllergyDialogOpen ? this.renderAllergyDialog() : null}
                {this.state.isFlagDialogOpen ? this.renderFlagDialog() : null}
                <form className="viewer-root input-form" onSubmit={this.handleFormSubmit}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h2>Base information</h2>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-first">First Name</label>
                                    <input type="text" id="frm-name-first" name="name.first" value={data.name?.first || ""} onChange={this.formInputChanged} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-last">Last Name</label>
                                    <input type="text" id="frm-name-last" name="name.last" value={data.name?.last || ""} onChange={this.formInputChanged} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-middle">Middle Name</label>
                                    <input type="text" id="frm-name-middle" name="name.middle" value={data.name?.middle || ""} onChange={this.formInputChanged} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-birthdate">Birthdate</label>
                                    <input type="date" id="frm-birthdate" name="birthdate" value={data.birthdate} onChange={this.formInputChanged} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-sex">Sex</label>
                                    <select id="frm-sex" name="sex" value={data.sex || ""} onChange={this.formInputChanged}>
                                        <option value="">Unspecified</option>
                                        <option value="M">Male</option>
                                        <option value="F">Female</option>
                                    </select>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-birthdate">Marital Status</label>
                                    <select id="frm-marital-status" name="maritalStatus" value={data.maritalStatus == null ? "" : data.maritalStatus} onChange={this.formInputChangedNumber}>
                                        <option value="">Unspecified</option>
                                        {getMaritalStatusValues().map(a => <option key={a.key} value={a.key}>{a.text}</option>)}
                                    </select>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-race">Race</label>
                                    <select id="frm-race" name="race" value={data.race || ""} onChange={this.formInputChanged}>
                                        <option value="0">Unknown</option>
                                        <option value="1">Declined</option>
                                        <option value="2">Other</option>
                                        <option value="100">White</option>
                                        <option value="200">Black / African American</option>
                                        <option value="300">American Indian / Alaskan Native</option>
                                        <option value="400">Asian</option>
                                        <option value="500">Native Hawaiian / Pacific Islander</option>
                                        <option value="600">Hispanic / Latino</option>
                                    </select>
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-insurance">Insurance Company</label>
                                    <ValidateInput type="select-other" id="frm-insurance" name="insuranceCompany" value={data.insuranceCompany} onChange={this.formInputChanged} autoComplete="off_please_chrome">
                                        <option value="" />
                                        {this.state.insuranceCompanies.map((a: any) => <option key={a.id} value={a.shortName || a.name}>{a.name}</option>)}
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group col-37-5">
                                    <label htmlFor="frm-phone">Phone Number</label>
                                    <input type="text" id="frm-phone" name="phoneNumber" value={formatTelephonePartial(data.phoneNumber) || ""} onChange={this.formInputChanged} />
                                </div>
                                <div className="input-form-group col-62-5">
                                    <label htmlFor="frm-email">Email Address</label>
                                    <input type="text" id="frm-email" name="emailAddress" value={data.emailAddress || ""} onChange={this.formInputChanged} />
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h2>Address</h2>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-address-line1">Address line 1</label>
                                    <input type="text" id="frm-address-line1" name="address.line1" value={data.address?.line1 || ""} onChange={this.formInputChanged} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-address-line2">Address line 2 <small>(optional)</small></label>
                                    <input type="text" id="frm-address-line2" name="address.line2" value={data.address?.line2 || ""} onChange={this.formInputChanged} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group col-50">
                                    <label htmlFor="frm-address-city">City</label>
                                    <input type="text" id="frm-address-city" name="address.city" value={data.address?.city || ""} onChange={this.formInputChanged} />
                                </div>
                                <div className="input-form-group col-12-5">
                                    <label htmlFor="frm-address-state">State</label>
                                    <input type="text" id="frm-address-state" className="uppercase" name="address.state" value={data.address?.state || ""} onChange={this.formInputChanged} />
                                </div>
                                <div className="input-form-group col-37-5">
                                    <label htmlFor="frm-address-zip">Zip</label>
                                    <input type="tel" id="frm-address-zip" name="address.zip" value={data.address?.zip || ""} onChange={this.formInputChanged} />
                                </div>
                            </section>
                        </section>
                    </div>
                    &nbsp;
                    <div className="column-root">
                        <div className="row-root">
                            <table className="section-table table-vertical">
                                <thead>
                                    <tr>
                                        <th colSpan={4}>Allergies</th>
                                    </tr>
                                    <tr>
                                        <th colSpan={2} className="col-smallest">Severity</th>
                                        <th>Allergen</th>
                                        <th>Reaction</th>
                                    </tr>
                                </thead>
                                <thead className="table-vertical-head-small">
                                    <tr>
                                        <th>Allergies</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.allergies.length === 0
                                        ? <tr><td colSpan={3}>No allergies listed</td></tr>
                                        : this.state.allergies.map((a, i) => <tr key={a.id} className={`clickable ${i === this.state.selectedAllergy ? "row-ltblue text-bold" : ""}`} onClick={() => this.setSelectedAllergy(i)}>
                                            <td className="col-smallest inline" data-caption="Severity: ">{allergySeverityToText(a.severity)}</td>
                                            <td className="col-smallest">{this.allergySeverityToIcon(a.severity)}</td>
                                            <td>{a.value}</td>
                                            <td>{a.reaction}</td>
                                        </tr>)}
                                </tbody>
                            </table>
                            <section className="section-bare">
                                <div className="section-bare action-panel-dialog">
                                    <button type="button" className="button text-with-icon" onClick={this.toggleAllergyDialog}><i className="icon-plus" /><span>Add Allergy</span></button>
                                    <button type="button" className="button text-with-icon" disabled={this.state.selectedAllergy === null} onClick={this.editAllergy}><i className="icon-pencil" /><span>Edit Allergy</span></button>
                                    <button type="button" className="button text-with-icon red" disabled={this.state.selectedAllergy === null} onClick={this.deleteAllergy}><i className="icon-times" /><span>Remove Allergy</span></button>
                                </div>
                            </section>
                        </div>
                        <div className="row-root">
                            <table className="section-table table-vertical align-top">
                                <thead className="table-vertical-head-always">
                                    <tr>
                                        <th colSpan={2}>Flags</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.flags.length === 0
                                        ? <tr><td colSpan={2}>No flags</td></tr>
                                        : this.state.flags.map((a, i) => <tr key={a.id} className={`clickable ${i === this.state.selectedFlag ? "row-ltblue" : ""}`} onClick={() => this.setSelectedFlag(i)}>
                                            <td className="col-smallest">{this.renderFlag(a)}</td>
                                            <td className="wrap">{a.description}</td>
                                        </tr>)}
                                </tbody>
                            </table>
                            <section className="section-bare">
                                <div className="section-bare action-panel-dialog">
                                    <button type="button" className="button text-with-icon" onClick={this.toggleFlagDialog}><i className="icon-plus" /><span>Add Flag</span></button>
                                    <button type="button" className="button text-with-icon red" disabled={this.state.selectedFlag === null} onClick={this.deleteFlag}><i className="icon-times" /><span>Remove Flag</span></button>
                                </div>
                            </section>
                        </div>
                    </div>
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : null}
                    <div className="action-panel-dialog">
                        <span className="spring" />
                        {this.state.hasChanged ?
                            <>
                                <button type="submit" className="button green" disabled={this.state.isSubmitting === true}>Save</button>
                                <button type="button" className="button" onClick={this.goBack}>Cancel</button>
                            </>
                            : <button type="button" className="button" onClick={this.goBack}>OK</button>}
                    </div>
                </form>
            </div>
        );
    }

    private renderAllergyDialog() {
        return <ReactModal isOpen={this.state.isAllergyDialogOpen} onRequestClose={this.toggleAllergyDialog}
            overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Add/edit allergy">
            <form className="input-form" onSubmit={this.saveAllergy}>
                <section className="info-block">
                    <header>
                        <h3>Add/Edit allergy</h3>
                    </header>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-allergy-value">Allergen</label>
                            <input type="text" id="frm-allergy-value" name="newAllergyValue" value={this.state.newAllergyValue} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" autoFocus={true} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-allergy-reaction">Reaction</label>
                            <input type="text" id="frm-allergy-reaction" name="newAllergyReaction" value={this.state.newAllergyReaction} onChange={this.dialogInputChanged} autoComplete="off_please_chrome" />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-allergy-severity">Severity</label>
                            <select id="frm-allergy-severity" name="newAllergySeverity" value={this.state.newAllergySeverity} onChange={this.dialogInputChanged} autoComplete="off_please_chrome">
                                <option value="">Select...</option>
                                <option value="0">Unknown</option>
                                <option value="1">Low</option>
                                <option value="2">High</option>
                            </select>
                        </div>
                    </section>
                    <br />
                    <div className="action-panel-dialog">
                        <button type="submit" className="button green push-right">OK</button>
                        <button type="button" className="button" onClick={this.toggleAllergyDialog}>Close</button>
                    </div>
                </section>
            </form>
        </ReactModal>;
    }

    private renderFlagDialog() {
        return <ReactModal isOpen={this.state.isFlagDialogOpen} onRequestClose={this.toggleFlagDialog}
            overlayClassName="modal-overlay" className="modal-content modal-content-500" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Add flag">
            <form className="input-form" onSubmit={this.saveFlag}>
                <section className="info-block">
                    <header>
                        <h3>Add flag</h3>
                    </header>
                    {this.state.isFlagsLoading === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isFlagsLoading === "string"
                            ? <div className="error-text">{this.state.isFlagsLoading}</div>
                            : <table className="table-vertical table-bare hover">
                                <tbody>
                                    {this.state.availableFlags.filter(a => !this.state.flags.some(b => b.id === a.id)).map(a => <tr key={a.id} className={`clickable ${this.state.flagAddList.includes(a.id) ? "row-ltblue" : ""}`} onClick={() => this.toggleSelectedFlag(a.id)}>
                                        <td>{this.renderFlag(a)}</td>
                                        <td className="wrap">{a.description}</td>
                                    </tr>)}
                                </tbody>
                            </table>}
                    <br />
                    <div className="action-panel-dialog">
                        <button type="submit" className="button green push-right">OK</button>
                        <button type="button" className="button" onClick={this.toggleFlagDialog}>Close</button>
                    </div>
                </section>
            </form>
        </ReactModal>;
    }

    private renderFlag(a: any) {
        return <Indicator key={a.id} color={a.color} text={a.title} icon={a.icon} title={a.description} />;
    }

    private formInputChangedNumber(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        this.formInputChanged(e, value => value === "" ? null : +value);
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, converter?: (value: any) => any) {
        let name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        let data = this.state.data;
        if (name.includes(".")) {
            const segments = name.split(".");
            [name] = segments.splice(segments.length - 1, 1);

            for (const seg of segments) {
                if (!data[seg]) {
                    data[seg] = {};
                }
                data = data[seg];
            }
        }

        data[name] = converter ? converter(value) : value;

        this.setState(s => ({
            hasChanged: true,
            data: s.data
        }));
    }

    private dialogInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>, converter?: (value: any) => any) {
        const name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private async loadData() {
        try {
            this.setState({
                isLoading: true
            });

            const [result, allergyResult, flagsResult, insuranceCompanies] = await Promise.all([
                V2.get<any>(`patients/${this.props.match.params.id}`),
                V2.get<any>(`patients/${this.props.match.params.id}/allergies`),
                V2.get<any>(`patients/${this.props.match.params.id}/flags`),
                V2.get<any>("accounting/insurance-companies/summary"),
            ]);
            result.birthdate = result.birthdate.substr(0, 10); // Trim off time, as the date input cannot handle it

            this.setState({
                isLoading: false,
                originalData: result,
                data: deepCopy(result),
                allergies: allergyResult.allergies,
                flags: flagsResult.flags,
                insuranceCompanies
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not load data"
            });
        }
    }

    private async handleFormSubmit(event: FormEvent) {
        event.preventDefault();

        try {
            this.setState({
                isSubmitting: true
            });

            const diff = diffObjects(this.state.originalData, this.state.data);

            if (diff) {
                if (diff.phoneNumber) {
                    diff.phoneNumber = diff.phoneNumber.replace(/[^0-9]/g, "");
                }

                await V2.patch<void>(`patients/${this.props.match.params.id}`, diff);
            }

            await this.submitAllergies();
            await this.submitFlags();

            this.setState({
                isSubmitting: false,
                hasChanged: false
            }, () => {
                this.props.history.goBack();
            });
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save changes"
            });
        }
    }

    private async submitAllergies() {
        if (Object.keys(this.state.allergyChanges).length === 0) {
            return;
        }

        const updates = [];
        const additions = [];
        const deletions = [];

        for (const key of Object.keys(this.state.allergyChanges)) {
            const change = this.state.allergyChanges[key];
            switch (change.type) {
                case "add":
                    additions.push(change.data);
                    break;
                case "delete":
                    deletions.push(change.id);
                    break;
                case "change":
                    const obj = Object.assign({}, change.data);
                    obj.id = change.id;
                    updates.push(obj);
                    break;
            }
        }

        const data = {
            updates: updates,
            additions: additions,
            deletions: deletions
        };

        await V2.patch(`patients/${this.props.match.params.id}/allergies`, data);
    }

    private async submitFlags() {
        if (Object.keys(this.state.flagChanges).length === 0) {
            return;
        }

        const additions = [];
        const deletions = [];

        for (const key of Object.keys(this.state.flagChanges)) {
            const change = this.state.flagChanges[key];
            switch (change.type) {
                case "add":
                    additions.push(change.id);
                    break;
                case "delete":
                    deletions.push(change.id);
                    break;
            }
        }

        const data = {
            additions: additions,
            deletions: deletions
        };

        await V2.patch(`patients/${this.props.match.params.id}/flags`, data);
    }

    private goBack() {
        this.props.history.goBack();
    }

    private allergySeverityToIcon(severity: number) {
        switch (severity) {
            case 0: return <i className="icon-question" />;
            case 1: return null;
            case 2: return <i className="icon-warning red" />;
            default: return null;
        }
    }

    private setSelectedAllergy(index: number | null) {
        this.setState(s => ({
            selectedAllergy: s.selectedAllergy === index ? null : index
        }));
    }

    private toggleAllergyDialog() {
        const isAllergyDialogOpen = this.state.isAllergyDialogOpen;
        if (isAllergyDialogOpen) {
            this.setState({
                isAllergyDialogOpen: false,
                selectedAllergy: null,
                newAllergySeverity: "",
                newAllergyValue: "",
                newAllergyReaction: "",
            });
        }
        else {
            this.setState({
                isAllergyDialogOpen: true,
            });
        }
    }

    private editAllergy() {
        if (this.state.selectedAllergy === null) {
            return;
        }

        const allergy = this.state.allergies[this.state.selectedAllergy];

        this.setState({
            isAllergyDialogOpen: true,
            hasChanged: true,
            newAllergySeverity: allergy.severity,
            newAllergyValue: allergy.value,
            newAllergyReaction: allergy.reaction,
        });
    }

    private deleteAllergy() {
        const { allergies, allergyChanges } = this.state;

        const [removed] = allergies.splice(this.state.selectedAllergy!, 1);

        if (!removed.id.startsWith("new_")) {
            allergyChanges[removed.id] = {
                type: "delete",
                id: removed.id
            };
        }
        else if (allergyChanges[removed.id]) {
            // Newly-added items can just be removed locally, as the server doesn't know about them
            delete allergyChanges[removed.id];
        }

        this.setState({
            hasChanged: true,
            selectedAllergy: null,
            allergyChanges,
            allergies
        });
    }

    private saveAllergy(e: FormEvent) {
        e.preventDefault();

        const allergies = this.state.allergies;
        const allergyChanges = this.state.allergyChanges;

        if (this.state.selectedAllergy !== null) {
            // Editing an allergy
            const existingAllergy = allergies[this.state.selectedAllergy];
            const allergy = {
                id: existingAllergy.id,
                severity: +this.state.newAllergySeverity,
                value: this.state.newAllergyValue,
                reaction: this.state.newAllergyReaction,
                dateRecorded: existingAllergy.dateRecorded,
            };

            const diff = diffObjects(existingAllergy, allergy);
            if (diff) {
                allergyChanges[existingAllergy.id] = {
                    type: "change",
                    id: existingAllergy.id,
                    data: diff
                };
            }

            allergies[this.state.selectedAllergy] = allergy;
        }
        else {
            // Adding a new allergy
            const newId = `new_${Date.now()}`;
            allergyChanges[newId] = {
                type: "add",
                id: newId,
                data: {
                    severity: +this.state.newAllergySeverity,
                    value: this.state.newAllergyValue,
                    reaction: this.state.newAllergyReaction,
                }
            };

            allergies.push({
                id: newId,
                severity: +this.state.newAllergySeverity,
                value: this.state.newAllergyValue,
                reaction: this.state.newAllergyReaction,
            });
        }

        this.setState({
            hasChanged: true,
            isAllergyDialogOpen: false,
            selectedAllergy: null,
            newAllergySeverity: "",
            newAllergyValue: "",
            newAllergyReaction: "",
            allergies,
            allergyChanges,
        });
    }

    private setSelectedFlag(index: number | null) {
        this.setState(s => ({
            selectedFlag: s.selectedFlag === index ? null : index
        }));
    }

    private toggleFlagDialog() {
        const isFlagDialogOpen = this.state.isFlagDialogOpen;
        this.setState({
            isFlagDialogOpen: !isFlagDialogOpen,
            isFlagsLoading: !isFlagDialogOpen,
            selectedFlag: null,
        }, () => {
            if (!isFlagDialogOpen) {
                // Start loading available flags
                V2.get<any>(`patients/flags`)
                    .then(result => {
                        this.setState({
                            isFlagsLoading: false,
                            availableFlags: result.flags
                        });
                    })
                    .catch(e => {
                        this.setState({
                            availableFlags: e.message || "Could not load flags"
                        });
                    });
            }
        });
    }

    private deleteFlag() {
        const { flags, flagChanges } = this.state;

        const [deleted] = flags.splice(this.state.selectedFlag!, 1);

        if (flagChanges[deleted.id]?.type === "add") {
            delete flagChanges[deleted.id];
        }
        else {
            flagChanges[deleted.id] = {
                type: "delete",
                id: deleted.id
            };
        }

        this.setState({
            hasChanged: true,
            selectedFlag: null,
            flags,
            flagChanges
        });
    }

    private saveFlag(e: FormEvent) {
        e.preventDefault();

        const { flagAddList, flags, flagChanges } = this.state;
        const flagsToAdd = this.state.availableFlags.filter(a => flagAddList.includes(a.id));

        Array.prototype.push.apply(flags, flagsToAdd);

        for (const id of flagAddList) {
            if (flagChanges[id]?.type === "delete") {
                // Re-adding a deleted flag is a no-op
                delete flagChanges[id];
            }
            else {
                flagChanges[id] = {
                    type: "add",
                    id: id,
                    data: null
                };
            }
        }

        this.setState({
            hasChanged: true,
            isFlagDialogOpen: false,
            flagAddList: [],
            flags,
            flagChanges
        });
    }

    private toggleSelectedFlag(id: string) {
        const flagAddList = this.state.flagAddList;

        const index = flagAddList.indexOf(id);
        if (index !== -1) {
            flagAddList.splice(index, 1);
        }
        else {
            flagAddList.push(id);
        }

        this.setState({
            flagAddList
        });
    }
}


export default withTitle("Update Patient Information", PatientEditView);
